import type { FieldSubject, FieldValue, FieldView } from "@fieldpath/shared";
import { mdy } from "@fieldpath/shared";
import { useCallback, useEffect, useState } from "react";

import { api, ApiError, type Fetcher } from "../api/client";
import { PAST_DATE_BOX } from "./DateHint";
import { EditChip } from "./EditChip";

/**
 * CUSTOM FIELDS on a project, deliverable, task or stage (E3/4).
 *
 * Loads its own fields through `GET /fields/:level/:id` — `fieldsFor` on the
 * server, which has already left out what this person may not see — and
 * saves each one through the ordinary write. A field the person may not
 * change is shown as text; a RESTRICTED field says so, because somebody who
 * can read it should know not everyone can.
 *
 * Renders nothing when the subject has no fields, so a page with none looks
 * exactly as it did.
 */
export function SubjectFields({
  fetcher,
  subject,
  title,
  locked = false,
}: {
  fetcher: Fetcher;
  subject: FieldSubject;
  title: string;
  /**
   * Shown as text until the EDIT chip is pressed (punch list #2, 12 Oct
   * 2026: "so people can't accidentally click or edit things they don't
   * mean to"). Off by default; every details panel turns it on -- the project
   * since #2, deliverable, task and stage since Jeremy's yes the same day.
   */
  locked?: boolean;
}) {
  const [fields, setFields] = useState<readonly FieldView[] | null>(null);
  const [errors, setErrors] = useState<Readonly<Record<string, string>>>({});
  // Not remembered: a form left open from last time invites an edit nobody
  // came to make -- the same reasoning as the deliverable's Details form.
  const [unlocked, setUnlocked] = useState(false);

  const load = useCallback(() => {
    void api
      .fields(fetcher, subject)
      .then(setFields)
      .catch(() => setFields([]));
    // The subject's two parts, not the object: a caller passing a fresh
    // literal each render must not reload the panel each render.
  }, [fetcher, subject.level, subject.id]);

  useEffect(load, [load]);

  if (!fields || fields.length === 0) return null;

  // The chip is offered only where something could be changed; a panel of
  // fields this person may only read has nothing to unlock.
  const anyEditable = fields.some((f) => f.mayEdit);
  const editing = !locked || unlocked;

  const save = async (field: FieldView, value: FieldValue): Promise<void> => {
    try {
      await api.setField(fetcher, subject, field.id, value);
      setErrors((e) => ({ ...e, [field.id]: "" }));
      load();
    } catch (e) {
      setErrors((x) => ({ ...x, [field.id]: e instanceof ApiError ? e.message : String(e) }));
    }
  };

  return (
    <section className="fields-panel">
      <div className="section-label" style={{ marginTop: 0 }}>
        <span>
          {title}
          {/* EDIT and DONE are the same chip in the same place, so locking
              and unlocking never moves the panel -- a full-size button here
              made the heading 13px taller and the rows jumped. Rendered. */}
          {locked && anyEditable ? (
            <EditChip
              label={unlocked ? "DONE" : "EDIT"}
              title={unlocked ? `Lock ${title.toLowerCase()}` : `Edit ${title.toLowerCase()}`}
              pressed={unlocked}
              onClick={() => setUnlocked((u) => !u)}
            />
          ) : null}
        </span>
      </div>
      {fields.map((f) => (
        <div key={f.id} className="field-row">
          <div className="field-row-label">
            <span>{f.label}</span>
            {f.required && f.value === null ? <span className="field-missing">MISSING</span> : null}
            {f.restricted ? (
              <span className="field-restricted" title="Not everybody can read this">
                RESTRICTED
              </span>
            ) : null}
          </div>
          <div className="field-row-value">
            {f.mayEdit && editing ? (
              <FieldInput field={f} onSave={(v) => void save(f, v)} />
            ) : (
              <span className={f.value === null ? "muted" : undefined}>{shownValue(f)}</span>
            )}
            {f.helpText && f.mayEdit && editing ? <p className="muted field-help">{f.helpText}</p> : null}
            {errors[f.id] ? <p className="error-inline">{errors[f.id]}</p> : null}
          </div>
        </div>
      ))}
    </section>
  );
}

/** A value as text, for somebody who may read it but not change it. */
export function shownValue(f: FieldView): string {
  if (f.value === null) return "—";
  if (f.type === "checkbox") return f.value ? "Yes" : "No";
  if (f.type === "dropdown") {
    const ids = f.value as readonly string[];
    return ids.length === 0 ? "—" : ids.map((id) => f.options.find((o) => o.id === id)?.label ?? "?").join(", ");
  }
  if (f.type === "date") return mdy(String(f.value));
  return String(f.value);
}

/**
 * The control for one field, by type. Choices, ticks and dates save as they
 * change; typed text and numbers save when the box is left, so a half-typed
 * value is never stored.
 */
export function FieldInput({
  field,
  onSave,
  compact = false,
}: {
  field: FieldView;
  onSave: (value: FieldValue) => void;
  compact?: boolean;
}) {
  const [draft, setDraft] = useState(
    field.value === null ? (field.defaultValue ?? "") : String(field.value),
  );
  useEffect(() => {
    setDraft(field.value === null ? (field.defaultValue ?? "") : String(field.value));
  }, [field.value, field.defaultValue]);

  switch (field.type) {
    case "checkbox":
      return (
        <input
          type="checkbox"
          aria-label={field.label}
          className="field-check"
          checked={field.value === true}
          onChange={(e) => onSave(e.target.checked)}
        />
      );
    case "date":
      return (
        <span className="date-with-warning">
          <input
            type="date"
            {...PAST_DATE_BOX}
            aria-label={field.label}
            value={(field.value as string | null) ?? ""}
            onChange={(e) => onSave(e.target.value === "" ? null : e.target.value)}
          />
        </span>
      );
    case "dropdown": {
      const held = (field.value as readonly string[] | null) ?? [];
      // A retired choice stays visible where it is held, never offered anew.
      const offered = field.options.filter((o) => o.isActive || held.includes(o.id));
      if (field.multiSelect) {
        return (
          <div className={compact ? "field-multi compact" : "field-multi"}>
            {offered.map((o) => (
              <label key={o.id} className="field-multi-option">
                <input
                  type="checkbox"
                  checked={held.includes(o.id)}
                  onChange={(e) =>
                    onSave(e.target.checked ? [...held, o.id] : held.filter((id) => id !== o.id))
                  }
                />
                {o.label}
              </label>
            ))}
          </div>
        );
      }
      return (
        <select
          aria-label={field.label}
          value={held[0] ?? ""}
          onChange={(e) => onSave(e.target.value === "" ? [] : [e.target.value])}
        >
          <option value="">—</option>
          {offered.map((o) => (
            <option key={o.id} value={o.id}>
              {o.label}
            </option>
          ))}
        </select>
      );
    }
    case "long_text":
      return (
        <textarea
          aria-label={field.label}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={() => {
            if (draft !== (field.value ?? "")) onSave(draft === "" ? null : draft);
          }}
        />
      );
    case "number":
    case "text":
      return (
        <input
          type={field.type === "number" ? "number" : "text"}
          aria-label={field.label}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={() => {
            const was = field.value === null ? "" : String(field.value);
            if (draft === was) return;
            if (draft.trim() === "") onSave(null);
            else onSave(field.type === "number" ? Number(draft) : draft);
          }}
        />
      );
  }
}
